import { Link } from "react-router-dom";
import { COMPANY } from "../../data/company";
import SEO from "../../components/SEO";
import ServiceGallerySection from "../../components/ServiceGallerySection";
import ContactForm from "../../components/ContactForm";
import { imageUrl } from "../../lib/images";

export default function DoorsWindows() {
  return (
    <>
      <SEO
        title="Døre og vinduer"
        description={`Udskiftning og montering af døre og vinduer i ${COMPANY.city} og Fyn af ${COMPANY.name}. Energieffektive løsninger og præcis finish. Ring ${COMPANY.phone}.`}
        path="/ydelser/doere-og-vinduer"
        image="/_assets/doere-vinduer-parcelhus.jpg"
      />

      {/* Hero header for Døre og vinduer */}
      <section className="bg-[#111111] text-white py-16 sm:py-24 border-b border-neutral-800 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/doere-vinduer-parcelhus.jpg", "width=1920,quality=80,format=auto")}
            alt="Døre og vinduer i dansk bolig"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-[#111111]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <Link to="/ydelser" className="hover:underline">Ydelser</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Døre og vinduer</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Døre og vinduer – vores store passion
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Vi elsker at lave døre og vinduer. Udskiftning af utætte eller udtjente elementer giver et markant løft i boligens komfort, sparer på varmeregningen og forvandler hjemmets udtryk. Vi sørger for millimeterpræcis opmåling og kompromisløs faglig stolthed fra start til slut.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={COMPANY.phoneLink}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#A78E51] text-white font-extrabold text-sm hover:bg-[#937B43] transition-colors"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring direkte: {COMPANY.phone}</span>
              </a>
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-neutral-600 transition-colors"
              >
                <span>Indhent tilbud på montage</span>
                <i className="fa-solid fa-arrow-down text-xs text-[#A78E51]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-6 text-neutral-700 text-sm sm:text-base leading-relaxed">
              <div>
                <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-1">
                  Vores absolutte hjertebarn
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight">
                  Vi elsker arbejdet med døre og vinduer
                </h2>
              </div>

              <p>
                Spørger du Andreas, er der intet mere tilfredsstillende end at montere nye døre og vinduer, hvor hver eneste karm sidder snorlige i vater, fugerne er ubrudte og de indvendige gerigter og lysninger slutter knivskarpt til. Det er tømrerhåndværk i sin reneste form.
              </p>
              <p>
                At skifte vinduer og døre er en investering, der skal holde i mange årtier. Derfor er den håndværksmæssige montage mindst lige så vigtig som selve elementernes kvalitet. Karme skal monteres i lod og vage, isoleres korrekt omkring falsen og fuges med egnede materialer for at undgå kuldebroer og træk.
              </p>
              <p>
                Vi hjælper med udskiftning af alle typer elementer i danske parcelhuse, villaer og ældre ejendomme – uanset om du ønsker klassiske trævinduer med sprosser eller moderne, vedligeholdelseslette træ/alu-elementer.
              </p>

              <div className="bg-[#FAFAF8] border border-neutral-200 rounded-lg p-6 my-6">
                <h3 className="font-bold text-base text-[#111111] mb-3 flex items-center gap-2">
                  <i className="fa-solid fa-door-open text-[#A78E51]" aria-hidden="true" />
                  <span>Vores opgaver med døre og vinduer:</span>
                </h3>
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                    <span>Nøjagtig opmåling og rådgivning om valg af elementer</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                    <span>Afmontering og bortskaffelse af gamle elementer</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                    <span>Professionel montage af facadedøre, terrassedøre og skydeelementer</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                    <span>Korrekt isolering og elastisk fugning omkring karme</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                    <span>Flotte tilpasninger af lysninger, sålbænke og gerigter</span>
                  </li>
                </ul>
              </div>

              <p>
                Resultatet er en bolig med et pænt finish både indefra og udefra – og en tæt løsning, du har glæde af i hverdagen.
              </p>
            </div>

            {/* Side visual card */}
            <div className="lg:col-span-5">
              <div className="bg-[#111111] text-white rounded-xl p-8 border border-neutral-800 sticky top-28">
                <div className="w-12 h-12 rounded-lg bg-[#1A1A1A] border border-[#A78E51]/50 flex items-center justify-center text-[#A78E51] text-xl mb-6">
                  <i className="fa-solid fa-door-open" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Trænger vinduerne til fornyelse?
                </h3>
                <p className="text-neutral-300 text-sm leading-relaxed mb-6">
                  Kontakt Andreas for en snak om udskiftning af døre eller vinduer i din bolig. Vi gennemgår opgaven og giver et uforpligtende prisoverslag.
                </p>

                <div className="space-y-4 pt-4 border-t border-neutral-800 text-sm">
                  <a
                    href={COMPANY.phoneLink}
                    className="flex items-center gap-3 text-white hover:text-[#A78E51] transition-colors"
                  >
                    <i className="fa-solid fa-phone text-[#A78E51]" aria-hidden="true" />
                    <span className="font-bold">{COMPANY.phone}</span>
                  </a>
                  <a
                    href={COMPANY.emailLink}
                    className="flex items-center gap-3 text-neutral-300 hover:text-white transition-colors break-all"
                  >
                    <i className="fa-solid fa-envelope text-[#A78E51]" aria-hidden="true" />
                    <span>{COMPANY.email}</span>
                  </a>
                  <div className="flex items-center gap-3 text-neutral-400">
                    <i className="fa-solid fa-location-dot text-[#A78E51]" aria-hidden="true" />
                    <span>{COMPANY.address}</span>
                  </div>
                </div>

                <div className="mt-8">
                  <a
                    href={COMPANY.phoneLink}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-md bg-[#A78E51] text-white font-bold text-sm hover:bg-[#937B43] transition-colors"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Ring til Andreas: {COMPANY.phone}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery linked to CMS slug 'doere-og-vinduer' */}
      <ServiceGallerySection
        gallerySlug="doere-og-vinduer"
        title="Døre og vinduer"
        subtitle="Billeder af montering og dør- og vinduesløsninger"
        fallbackImages={[
          "/_assets/doere-vinduer-parcelhus.jpg",
          "/_assets/doere-vinduer-terrasse.jpg",
        ]}
        fallbackCaption="Udskiftning og præcis montering af vinduer og terrassedøre"
        fallbackAlt="Montering af energieffektive døre og vinduer i høj kvalitet"
      />

      {/* Contact Section */}
      <section id="kontakt-sektion" className="py-16 sm:py-24 bg-white border-t border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-2">
              Forespørgsel på døre og vinduer
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111111] tracking-tight">
              Kontakt os for opmåling eller rådgivning
            </h2>
            <p className="mt-3 text-neutral-600 text-sm sm:text-base">
              Fortæl os om dit projekt, så kontakter vi dig hurtigt med svar på dine spørgsmål.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <ContactForm defaultService="Døre og vinduer" />
          </div>
        </div>
      </section>
    </>
  );
}
